import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'

/**
 * The coin case.
 *
 * Unlocks are decided entirely in the database: the client can read its case
 * and mark an unlock as seen, and nothing else. A client able to award itself a
 * coin could forge the whole collection, which would make the case worthless.
 */

export interface CaseCoin {
  id: string
  name: string
  group_key: string
  tier: 'bronze' | 'silver' | 'gold' | null
  description: string
  /** Null for discovery coins: the reference is explicit that their triggers
   *  must never be published in product. */
  trigger_text: string | null
  is_secret: boolean
  /** Seeds paid out the first time anyone on the account earns this coin.
   *  Zero for status-only coins, which is most of them. */
  reward_seeds: number
  /** How far along the account is toward this coin, in `progress_unit`. Null
   *  on coins that are a single moment rather than an accumulation, and on
   *  every discovery coin, whose trigger must not be inferable from a ring. */
  progress_current: number | null
  progress_goal: number | null
  progress_unit: 'stories' | 'weeks' | 'months' | 'places' | null
  unlocked_at: string | null
  seen_at: string | null
  sort_order: number
  /** Deep Roots only: completed years with the account. */
  tier_years: number | null
  /** When the person opened Achievements after earning it (badge spec §6:
   *  the unviewed count persists until Achievements is opened). */
  viewed_at?: string | null
  is_active?: boolean
}

/** A hidden coin not yet earned: an empty well marked "?" (my_case masks it). */
export const isHiddenCoin = (c: CaseCoin) => c.is_secret && !c.unlocked_at

export const COIN_GROUPS: { key: string; label: string; note: string }[] = [
  { key: 'post-ladder',  label: 'The harvest',   note: 'Stories out in the world' },
  { key: 'post-seasons', label: 'Seasons',       note: 'The long haul' },
  { key: 'retention',    label: 'Keeping time',  note: 'Weeks you showed up' },
  { key: 'loyalty',      label: 'Taking root',   note: 'Consistency over months' },
  { key: 'coverage',     label: 'New ground',    note: 'Where you cover' },
  { key: 'upgrades',     label: 'Growing',       note: 'Room to work' },
  { key: 'emma',         label: 'Counsel',       note: 'Working with the advisor' },
  { key: 'spend',        label: 'Investment',    note: 'Backing the work' },
  { key: 'media',        label: 'The press',     note: 'Beyond video' },
  { key: 'discovery',    label: 'Found',         note: 'Earned without chasing' },
  { key: 'fortune',      label: 'Fortune seekers', note: 'Coins for collecting coins' },
]

/**
 * The case, as the database chooses to show it.
 *
 * `my_case()` already filters: a secret coin stays hidden until earned, while
 * everything else shows its empty well so the set reads as gaps rather than
 * absence.
 */
export function useCoinCase() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['coin-case', userId],
    enabled: !!userId,
    queryFn: async (): Promise<CaseCoin[]> => {
      const { data, error } = await supabase.rpc('my_case')
      if (error) throw error
      return (data ?? []) as CaseCoin[]
    },
  })
}

/** Coins unlocked but never celebrated. Drives the unlock modal. */
export function useUnseenUnlocks() {
  const { data } = useCoinCase()
  return (data ?? []).filter((c) => c.unlocked_at && !c.seen_at)
}

/** Coins celebrated (the unlock dismissed) and not yet looked at in
 *  Achievements: the unviewed count, which grows on each dismiss. */
export function useUnviewedCoins() {
  const { data } = useCoinCase()
  return (data ?? []).filter((c) => c.unlocked_at && c.seen_at && !c.viewed_at)
}

/** Opening Achievements: every earned coin counts as viewed (and as
 *  celebrated, so one earned elsewhere does not play again on the feed). */
export function useMarkViewed() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const now = new Date().toISOString()
      const a = await supabase.from('user_achievements').update({ viewed_at: now } as never)
        .eq('user_id', userId!).is('viewed_at', null).not('unlocked_at', 'is', null)
      if (a.error) throw a.error
      const b = await supabase.from('user_achievements').update({ seen_at: now })
        .eq('user_id', userId!).is('seen_at', null).not('unlocked_at', 'is', null)
      if (b.error) throw b.error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['coin-case', userId] }) },
  })
}

/**
 * Marks an unlock celebrated so it does not fire again.
 *
 * The only write a user is permitted on the table, and it controls nothing
 * except whether the animation plays.
 */
export function useMarkSeen() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (achievementId: string) => {
      const { error } = await supabase
        .from('user_achievements')
        .update({ seen_at: new Date().toISOString() })
        .eq('achievement_id', achievementId)
        .eq('user_id', userId!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coin-case', userId] })
    },
  })
}

/**
 * Progress toward a coin, in words.
 *
 * Returns null when there is nothing honest to say: an earned coin, a coin with
 * no countable trigger, or one nobody has started on. A ring at zero is not
 * encouragement, it is an empty circle.
 */
export function coinProgress(coin: CaseCoin): { pct: number; label: string } | null {
  if (coin.unlocked_at) return null
  const { progress_current: at, progress_goal: goal, progress_unit: unit } = coin
  if (at == null || goal == null || !unit || at <= 0) return null

  const noun =
    unit === 'stories' ? (goal === 1 ? 'story' : 'stories')
    : unit === 'weeks' ? 'weeks in a row'
    : unit === 'places' ? (goal === 1 ? 'place' : 'places')
    : goal === 1 ? 'month' : 'months'

  return { pct: Math.min(100, Math.round((at / goal) * 100)), label: `${at} of ${goal} ${noun}` }
}

export function coinArt(id: string, unlocked: boolean, size: 256 | 512 = 256): string {
  return `/coins/${id}${unlocked ? '' : '-locked'}-${size}.webp`
}

// ---------------- Medals ----------------

/** A medal is held by one member at a time, then handed over. */
export interface Medal {
  medal_id: 'top-billing' | 'grand-marshal'
  name: string
  description: string
  unlock_line: string
  floor: number
  holder_name: string | null
  holder_is_me: boolean
  holder_count: number | null
  my_count: number
  since: string | null
}

export interface MedalRun {
  medal_id: string
  name: string
  first_month: string
  last_month: string
  months: number
}

export function useMedals() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['medals', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('my_medals')
      if (error) throw error
      return (data ?? []) as Medal[]
    },
  })
}

export function useMedalHistory() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['medal-history', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('my_medal_history')
      if (error) throw error
      return (data ?? []) as MedalRun[]
    },
  })
}

export function medalArt(id: string): string {
  return `/medals/${id}.webp`
}

// ---------------- Notices ----------------

export interface Notice {
  id: string
  kind: 'medal_won' | 'medal_lost' | 'medal_near' | 'anniversary'
  medal_id: string | null
  title: string
  body: string
  created_at: string
  seen_at: string | null
}

/**
 * Handovers, near misses and anniversary gifts. There is no server push yet,
 * so these surface in the app the next time it opens. Anniversaries are
 * checked lazily here, on open, rather than by any schedule.
 */
export function useNotices() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['notices', userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      await supabase.rpc('check_anniversaries')
      const { data, error } = await supabase
        .from('notices').select('*').eq('user_id', userId!).is('seen_at', null)
        .order('created_at', { ascending: true }).limit(10)
      if (error) throw error
      return (data ?? []) as Notice[]
    },
  })
}

export function useMarkNoticeSeen() {
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('notices').update({ seen_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notices', userId] })
      qc.invalidateQueries({ queryKey: ['coin-case', userId] })
    },
  })
}
